import { useState, useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import RiskComplianceCard from "./RiskComplianceCard";
import SuggestionButton from './UI/SuggestionButton';
import { useTranslation } from 'react-i18next';

export default function SecurityTabs({
  risposte,
  visibleQuestions = true,
}) {
  const [activeTab, setActiveTab] = useState("");
  const [openItems, setOpenItems] = useState({});

  const sezioni = useMemo(() => {
    if (!risposte?.sezioni) return [];
    return Object.entries(risposte.sezioni).map(([titolo, contenuto]) => ({
      titolo,
      descrizione: contenuto.descrizione,
      domande: contenuto.risposte,
      percentuale: contenuto.percentuale,
    }));
  }, [risposte]);

  useEffect(() => {
    if (sezioni.length > 0 && !activeTab) {
      setActiveTab(sezioni[0].titolo);
    }
  }, [sezioni, activeTab]);

  const { t } = useTranslation();

  if (!sezioni.length || !activeTab) {
    return <div>{t('securityTabs.noSections')}</div>;
  }

  const contenutoAttivo = sezioni.find((s) => s.titolo === activeTab);


  const toggleOpen = (id) =>
    setOpenItems((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="w-full flex-1 h-full flex flex-col">
      {/* Tabs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 justify-items-center mb-6">
        {sezioni.map((sez) => (
          <button
            key={sez.titolo}
            onClick={() => setActiveTab(sez.titolo)}
            className={`px-4 py-2 font-medium border-b-2 text-center flex items-center justify-center text-sm sm:text-sm w-full text-wrap break-words ${activeTab === sez.titolo
              ? "border-yellow-500 text-yellow-600"
              : "text-gray-500"
              }`}
          >
            <span className="block w-full">{(t(`nis2Categories.${sez.titolo}`, '') || sez.titolo).toUpperCase()}</span>
          </button>
        ))}
      </div>

      {/* Card dinamica */}
      <div className="flex-1 w-full bg-white rounded-lg flex flex-col min-h-[250px]">
        <RiskComplianceCard
          title={t(`nis2Categories.${contenutoAttivo.titolo}`, '') || contenutoAttivo.titolo}
          testo={t(`nis2Descriptions.${contenutoAttivo.titolo}`, '') || contenutoAttivo.descrizione}
          percentage={contenutoAttivo.percentuale}
        />
      </div>

      {/* Domande critiche */}
      {visibleQuestions && contenutoAttivo.domande?.length > 0 && contenutoAttivo.percentuale !== 100 && (
        <div className="mt-8 space-y-4">
          <p className="text-xl font-bold">
            {t('securityTabs.criticalityTitle')}
          </p>
          {contenutoAttivo.domande.map((domanda) =>
            domanda.opzione_selezionata?.testo.toUpperCase() !== "SI" ? (
              <div key={domanda.id} className="border p-4 rounded">
                <div className="flex justify-between items-start">
                  <p className="font-semibold">
                    {domanda.domanda_testo.trim()}
                  </p>
                  <SuggestionButton id={domanda.id} openItems={openItems} toggleOpen={toggleOpen} />
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {t('securityTabs.answer')}:{" "}
                  <span className="font-medium">
                    {domanda.opzione_selezionata?.testo}
                  </span>
                </p>
                <AnimatePresence initial={false}>
                  {openItems[domanda.id] && (
                    <motion.div
                      key="suggestion"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden border border-gray-400 rounded-md mt-5"
                    >
                      <div className="text-sm flex flex-col text-gray-700 p-5">
                        {domanda.remediation ? (
                          <div>
                            <div className="flex flex-col gap-3 mb-6">
                              <p className="font-semibold">{domanda.remediation.titolo}</p>
                              <p>{domanda.remediation.descrizione}</p>
                            </div>
                            <div className="flex flex-col gap-1">
                              <p className="italic">{t('securityTabs.firstSteps')}</p>
                              <p>{domanda.remediation.primi_passi}</p>
                            </div>
                          </div>
                        ) : (
                          <p className="italic">{t('securityTabs.noSuggestion')}</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : null
          )}
        </div>
      )}
    </div>
  );
}
